import "./styles/globals.css";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster, toast } from "sonner";
import { onSession } from "./api/auth";
import { ApiError, errorMessage } from "./api/client";
import { makeRouter } from "./app/router";
import { TooltipProvider } from "./components/ui/overlay";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
      refetchOnWindowFocus: true,
    },
  },
  queryCache: new QueryCache({
    onError: (e, q) => {
      if (q.meta?.silent || (e instanceof ApiError && (e.status === 401 || e.status === 404))) return;
      toast.error(errorMessage(e));
    },
  }),
  mutationCache: new MutationCache({
    onError: (e, _v, _c, m) => {
      if (m.meta?.silent || (e instanceof ApiError && e.status === 412)) return;
      toast.error(errorMessage(e));
    },
  }),
});

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}

const router = makeRouter(queryClient);

// Signed out (here or in another tab): leave protected pages.
onSession((s) => {
  if (!s) {
    queryClient.clear();
    const p = location.pathname;
    if (!["/login", "/invite", "/reset"].includes(p)) router.navigate({ to: "/login", search: { next: p + location.search } });
  }
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <RouterProvider router={router} />
        <Toaster
          position="bottom-right"
          toastOptions={{ className: "!bg-surface !text-fg !border-border !shadow-overlay text-sm" }}
        />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
